import React, { useState, useMemo, useCallback } from 'react';
import { Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';

/**
 * TimePicker — drop-in replacement for <Input type="time">.
 *
 * Internal value contract is identical to native <input type="time">:
 *   `value`  : "HH:MM" 24-hour string (or "" if unset)
 *   `onChange({ target: { value } })` : emits "HH:MM" 24-hour
 *
 * Display is 12-hour with AM/PM (e.g. "11:35 PM"). Minutes step by 5.
 * Backend payloads are unchanged.
 */

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES_STEP_5 = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,...,55

const pad2 = (n) => String(n).padStart(2, '0');

const parse24 = (val) => {
  // "HH:MM" -> { hour12, minute, period }; returns null if not parseable
  if (!val || typeof val !== 'string') return null;
  const [hStr, mStr] = val.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const period = h >= 12 ? 'PM' : 'AM';
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute: m, period };
};

const to24 = (hour12, minute, period) => {
  let h = hour12 % 12;
  if (period === 'PM') h += 12;
  return `${pad2(h)}:${pad2(minute)}`;
};

const formatDisplay = (val) => {
  const parsed = parse24(val);
  if (!parsed) return '';
  return `${parsed.hour12}:${pad2(parsed.minute)} ${parsed.period}`;
};

const Column = ({ items, selected, onSelect, format, testId }) => (
  <div
    className="flex-1 max-h-56 overflow-y-auto overscroll-contain border border-gray-200 rounded-md py-1"
    data-testid={testId}
  >
    {items.map((it) => {
      const isSel = it === selected;
      return (
        <button
          key={String(it)}
          type="button"
          onClick={() => onSelect(it)}
          className={`block w-full text-center py-1.5 text-sm transition-colors ${
            isSel
              ? 'bg-emerald-50 text-emerald-700 font-semibold'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          {format ? format(it) : it}
        </button>
      );
    })}
  </div>
);

const TimePicker = ({
  id,
  name,
  value = '',
  onChange,
  placeholder = 'Select time',
  className = '',
  'data-testid': dataTestId,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const parsed = useMemo(() => parse24(value), [value]);
  // Defaults when unset: 12:00 PM (rounded to nearest 5-min step)
  const initial = parsed || { hour12: 12, minute: 0, period: 'PM' };

  const [hour12, setHour12] = useState(initial.hour12);
  const [minute, setMinute] = useState(
    initial.minute - (initial.minute % 5) // snap to 5
  );
  const [period, setPeriod] = useState(initial.period);

  // Re-sync local state when external value changes (e.g. form reset)
  React.useEffect(() => {
    if (!open) {
      const p = parse24(value);
      if (p) {
        setHour12(p.hour12);
        setMinute(p.minute - (p.minute % 5));
        setPeriod(p.period);
      }
    }
  }, [value, open]);

  const emit = useCallback(
    (h, m, p) => {
      const next = to24(h, m, p);
      if (typeof onChange === 'function') {
        onChange({ target: { value: next, name } });
      }
    },
    [onChange, name]
  );

  const handleHour = (h) => {
    setHour12(h);
    emit(h, minute, period);
  };
  const handleMinute = (m) => {
    setMinute(m);
    emit(hour12, m, period);
  };
  const handlePeriod = (p) => {
    setPeriod(p);
    emit(hour12, minute, p);
  };

  const display = formatDisplay(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          variant="outline"
          disabled={disabled}
          data-testid={dataTestId}
          className={`w-full justify-start font-normal h-10 px-3 ${
            display ? 'text-gray-900' : 'text-gray-400'
          } ${className}`}
        >
          <Clock className="w-4 h-4 mr-2 text-gray-400 shrink-0" />
          <span className="truncate">{display || placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-3 z-[85]"
        data-testid="time-picker-popover"
      >
        <div className="text-xs font-medium text-gray-500 mb-2">Select time</div>
        <div className="flex gap-2">
          <Column
            items={HOURS_12}
            selected={hour12}
            onSelect={handleHour}
            testId="time-picker-hour-col"
          />
          <Column
            items={MINUTES_STEP_5}
            selected={minute}
            onSelect={handleMinute}
            format={pad2}
            testId="time-picker-minute-col"
          />
          <Column
            items={['AM', 'PM']}
            selected={period}
            onSelect={handlePeriod}
            testId="time-picker-period-col"
          />
        </div>
        <div className="flex justify-between mt-3">
          <button
            type="button"
            onClick={() => {
              if (typeof onChange === 'function') {
                onChange({ target: { value: '', name } });
              }
              setOpen(false);
            }}
            className="text-xs text-gray-500 hover:text-gray-700 px-1"
            data-testid="time-picker-clear"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-1"
            data-testid="time-picker-done"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TimePicker;
